// src/components/Admin/shared/ToastContext.jsx
import React, { createContext, useContext, useState, useCallback } from "react";
import { Check, AlertCircle, Info, X } from "lucide-react";

const ToastContext = createContext(null);

const ICONS = {
  success: Check,
  error: AlertCircle,
  info: Info,
};

const STYLES = {
  success: "bg-emerald-50 border-emerald-200 text-emerald-800",
  error: "bg-red-50 border-red-200 text-red-800",
  info: "bg-blue-50 border-blue-200 text-blue-800",
};

const ICON_STYLES = {
  success: "text-emerald-500",
  error: "text-red-500",
  info: "text-blue-500",
};

let toastId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = "success", duration = 4000) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type }]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    {
      success: (msg) => addToast(msg, "success"),
      error: (msg) => addToast(msg, "error", 6000),
      info: (msg) => addToast(msg, "info"),
    },
    [addToast],
  );

  // Memoize the value to prevent unnecessary re-renders
  const value = React.useMemo(() => toast, [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast container */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => {
          const Icon = ICONS[t.type] || Info;
          return (
            <div
              key={t.id}
              className={`pointer-events-auto flex items-center gap-2.5 px-4 py-3 border rounded-lg shadow-lg max-w-sm animate-slide-in ${STYLES[t.type]}`}
            >
              <Icon
                size={16}
                className={`flex-shrink-0 ${ICON_STYLES[t.type]}`}
              />
              <p className="text-sm flex-1">{t.message}</p>
              <button
                onClick={() => removeToast(t.id)}
                className="flex-shrink-0 opacity-50 hover:opacity-100 transition"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slideIn 0.25s ease-out;
        }
      `}</style>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback if used outside provider — log to console
    return {
      success: (msg) => console.log("[toast:success]", msg),
      error: (msg) => console.error("[toast:error]", msg),
      info: (msg) => console.info("[toast:info]", msg),
    };
  }
  return ctx;
};

export default ToastProvider;
